import type { BoardData, Item } from '../model/schema.js';
import { mainStage, worldPosition } from '../model/areas.js';
import { storeZip, utf8 } from './zip.js';

/**
 * MVR export for the lighting department (U49, OIL-23): an MVR 1.6 file (ZIP with
 * GeneralSceneDescription.xml) holding the board's lights as fixtures and its trusses, with
 * position (mm, origin = downstage centre, +y upstage, z up), rotation, height and DMX address.
 * No GDTF files are included – the lighting console maps the fixture types.
 */

const xml = (s: string) =>
  s.replace(
    /[<>&"']/g,
    (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' })[c]!,
  );

/** A stable RFC 4122-shaped UUID for any id (item ids already are UUIDs). */
export function mvrUuid(id: string): string {
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id))
    return id.toUpperCase();
  // FNV-1a over the id, spread to 128 bit
  const words: number[] = [];
  let h = 0x811c9dc5;
  for (let round = 0; round < 4; round++) {
    for (const ch of `${round}:${id}`) h = Math.imul(h ^ ch.charCodeAt(0), 0x01000193) >>> 0;
    words.push(h);
  }
  const hex = words.map((w) => w.toString(16).padStart(8, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-8${hex.slice(17, 20)}-${hex.slice(20, 32)}`.toUpperCase();
}

const mm = (m: number) => Math.round(m * 1000);

function matrix(data: BoardData, item: Item, height: number): string {
  // only placed items get here; origin = downstage centre of the main stage (OIL-281)
  const w = worldPosition(data, item)!;
  const main = mainStage(data);
  const x = main ? w.x - (main.x + main.width / 2) : w.x;
  const y = main ? main.y + main.depth - w.y : -w.y;
  // canvas rotation is clockwise with y down; MVR is counter-clockwise with y up
  const a = (-item.rotation * Math.PI) / 180;
  const c = Math.round(Math.cos(a) * 1e6) / 1e6;
  const s = Math.round(Math.sin(a) * 1e6) / 1e6;
  return `{${c},${s},0}{${-s},${c},0}{0,0,1}{${mm(x)},${mm(y)},${mm(height)}}`;
}

function heightOf(data: BoardData, item: Item): number {
  if (item.light?.height !== undefined) return item.light.height;
  if (item.truss?.height !== undefined) return item.truss.height;
  const on = item.mountedOn === undefined ? undefined : data.items[item.mountedOn];
  if (on?.truss?.height !== undefined) return on.truss.height;
  return item.z ?? 0;
}

/** DMX address as MVR counts it: (universe − 1) · 512 + address. */
export function mvrAddress(universe: number, address: number): number {
  return (universe - 1) * 512 + address;
}

/** GeneralSceneDescription.xml of the board's lights and trusses. */
export function mvrSceneXml(data: BoardData): string {
  const placed = Object.values(data.items).filter(
    (i) => worldPosition(data, i) !== undefined && !i.hidden,
  );
  const lights = placed.filter((i) => i.kind === 'light');
  const trusses = placed.filter((i) => i.kind === 'truss');
  const name = (i: Item) => xml(i.label || [i.brand, i.model].filter(Boolean).join(' ') || i.kind);
  const fixtures = lights.map((l, k) => {
    const dmx = l.light?.dmx;
    const address =
      dmx?.universe !== undefined && dmx.address !== undefined
        ? `<Addresses><Address break="0">${mvrAddress(dmx.universe, dmx.address)}</Address></Addresses>`
        : '<Addresses/>';
    return (
      `<Fixture name="${name(l)}" uuid="${mvrUuid(l.id)}">` +
      `<Matrix>${matrix(data, l, heightOf(data, l))}</Matrix>` +
      `<GDTFSpec>${xml([l.brand, l.model].filter(Boolean).join(' '))}</GDTFSpec>` +
      `<GDTFMode>${xml(dmx?.mode ?? '')}</GDTFMode>` +
      `<FixtureID>${k + 1}</FixtureID><UnitNumber>${k + 1}</UnitNumber>` +
      address +
      `</Fixture>`
    );
  });
  const truss = trusses.map(
    (t) =>
      `<Truss name="${name(t)}" uuid="${mvrUuid(t.id)}">` +
      `<Matrix>${matrix(data, t, heightOf(data, t))}</Matrix>` +
      `<GDTFSpec>${xml([t.brand, t.model].filter(Boolean).join(' '))}</GDTFSpec><GDTFMode></GDTFMode>` +
      `</Truss>`,
  );
  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n` +
    `<GeneralSceneDescription verMajor="1" verMinor="6" provider="stageplottter" providerVersion="1">` +
    `<UserData/><Scene><AUXData/><Layers>` +
    `<Layer name="${xml(data.meta.name || 'Stage')}" uuid="${mvrUuid(`layer:${data.meta.name}`)}">` +
    `<ChildList>${[...truss, ...fixtures].join('')}</ChildList>` +
    `</Layer></Layers></Scene></GeneralSceneDescription>\n`
  );
}

/** The MVR file (ZIP) of the board's lighting. */
export function mvrFile(data: BoardData): Uint8Array {
  return storeZip([{ name: 'GeneralSceneDescription.xml', data: utf8(mvrSceneXml(data)) }]);
}
